import * as React from 'react';
import MediaElement from './MediaElement';
import { ZoomedImage } from '../../../../types';
import CloseIcon from '../../../../../../svg/icons/remove.svg';
import ArrowIcon from '../../../../../../svg/icons/arrow-line-right.svg';

const { viewerMediaSet } = window.globalProductDetails;

interface ZoomerGalleryProps {
  zoomedImage: ZoomedImage;
  setZoomedImage: React.Dispatch<React.SetStateAction<ZoomedImage | undefined>>;
}

function ZoomerGallery({ zoomedImage, setZoomedImage }: ZoomerGalleryProps): React.ReactElement | null {
  const maxImageIndex = viewerMediaSet ? viewerMediaSet.items.length - 1 : 0;
  const [currentImageIndex, setCurrentImageIndex] = React.useState(zoomedImage.index);
  const unobservePrevImageRef: React.MutableRefObject<() => void> = React.useRef(() => {});
  const unobserveNextImageRef: React.MutableRefObject<() => void> = React.useRef(() => {});

  React.useLayoutEffect(() => {
    getImageEl(zoomedImage.index)?.scrollIntoView({ block: 'center' });
  }, []);

  React.useEffect(() => {
    unobservePrevImageRef.current();
    unobserveNextImageRef.current();
    observeCurrentImage();
    handleArrow('top', 0);
    handleArrow('bottom', maxImageIndex);
  }, [currentImageIndex]);

  React.useEffect(() => {
    disableViewportZoom();

    return (): void => {
      enableViewportZoom();
    };
  }, []);

  function disableViewportZoom(): void {
    window.addEventListener('wheel', preventWheelEvent, { passive: false });
    window.addEventListener('gesturestart', preventDefault);
    window.addEventListener('gesturechange', preventDefault);
    window.addEventListener('gestureend', preventDefault);
  }

  function enableViewportZoom(): void {
    window.removeEventListener('wheel', preventWheelEvent);
    window.removeEventListener('gesturestart', preventDefault);
    window.removeEventListener('gesturechange', preventDefault);
    window.removeEventListener('gestureend', preventDefault);
  }

  function preventWheelEvent(event: MouseEvent): void {
    if (event.ctrlKey) {
      event.preventDefault();
    }
  }

  function preventDefault(event: Event): void {
    event.preventDefault();
  }

  function handleArrow(arrow: 'top' | 'bottom', stopIndex: number): void {
    const arrowEl: HTMLButtonElement | null = document.querySelector(`.zoomer-gallery__arrow--${arrow}`);

    if (arrowEl) {
      arrowEl.disabled = currentImageIndex === stopIndex;
    }
  }

  function scrollToImage(index: number): void {
    getImageEl(index)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function getImageEl(index: number): Element | null {
    return document.querySelector(`.zoomer-gallery__image-wrapper[data-order='${index}']`);
  }

  function observeCurrentImage(): void {
    const prevImageObserverOptions = {
      root: document.querySelector('.zoomer-gallery__container'),
      rootMargin: '-50% 0px 0px 0px'
    };

    const nextImageObserverOptions = {
      root: document.querySelector('.zoomer-gallery__container'),
      rootMargin: '0px 0px -50% 0px'
    };

    const prevImageObserver = new IntersectionObserver(observerCallback, prevImageObserverOptions);
    const nextImageObserver = new IntersectionObserver(observerCallback, nextImageObserverOptions);
    const prevImageEl = getImageEl(currentImageIndex - 1);
    const nextImageEl = getImageEl(currentImageIndex + 1);

    unobservePrevImageRef.current = (): void | null => (prevImageEl ? prevImageObserver.unobserve(prevImageEl) : null);
    unobserveNextImageRef.current = (): void | null => (nextImageEl ? nextImageObserver.unobserve(nextImageEl) : null);

    function observerCallback(entries: IntersectionObserverEntry[]): void {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0 && entry.intersectionRatio !== 1) {
          if (entry.boundingClientRect.top < 0) {
            setCurrentImageIndex(() => currentImageIndex - 1);
          }

          if (entry.boundingClientRect.top > 0) {
            setCurrentImageIndex(() => currentImageIndex + 1);
          }
        }
      });
    }

    if (prevImageEl) {
      prevImageObserver.observe(prevImageEl);
    }

    if (nextImageEl) {
      nextImageObserver.observe(nextImageEl);
    }
  }

  return (
    <div className="zoomer-gallery">
      <div className="zoomer-gallery__container">
        <button className="zoomer-gallery__close-button" type="button" onClick={(): void => setZoomedImage(undefined)}>
          <CloseIcon />
        </button>
        <button
          className="zoomer-gallery__arrow zoomer-gallery__arrow--top"
          type="button"
          onClick={(): void => {
            if (currentImageIndex !== 0) {
              scrollToImage(currentImageIndex - 1);
            }
          }}
        >
          <ArrowIcon />
        </button>
        <button
          className="zoomer-gallery__arrow zoomer-gallery__arrow--bottom"
          type="button"
          onClick={(): void => {
            if (currentImageIndex !== maxImageIndex) {
              scrollToImage(currentImageIndex + 1);
            }
          }}
        >
          <ArrowIcon />
        </button>
        {viewerMediaSet?.items.map((media, index) => (
          <div className="zoomer-gallery__image-wrapper" key={`item_${index + 1}`} data-order={index}>
            <MediaElement index={index} media={media} className="zoomer-gallery" autoplay />
          </div>
        ))}
      </div>
    </div>
  );
}

export default ZoomerGallery;
